import React, { useEffect, useState } from "react";
import { Truck, Clock } from "lucide-react";

const formatRemaining = (ms) => {
    if (ms <= 0) return null;
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
    if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
    return `${s}s`;
};

/**
 * Dispatch countdown. Reads /api/settings to get the server-computed deadline
 * (in Sydney TZ, accounting for cutoff + weekends + holidays).
 * - Before deadline: shows the countdown ("Order within Xh Ym for Same-Day Dispatch")
 * - After deadline (or non-business day): shows the after-message
 *
 * Refreshes the deadline from the server every 60 seconds to recover from cutoff transitions.
 */
export const DispatchCountdown = ({ compact = false, className = "" }) => {
    const [settings, setSettings] = useState(null);
    const [now, setNow] = useState(Date.now());

    const fetchSettings = async () => {
        try {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/settings`;
            const r = await fetch(url);
            const data = await r.json();
            setSettings(data.dispatch);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        fetchSettings();
        const refresh = setInterval(fetchSettings, 60_000);
        const tick = setInterval(() => setNow(Date.now()), 1_000);
        return () => {
            clearInterval(refresh);
            clearInterval(tick);
        };
    }, []);

    if (!settings || !settings.enabled) return null;

    const deadlineMs = new Date(settings.deadline_iso).getTime();
    const remaining = deadlineMs - now;
    const isSameDay = settings.mode === "same_day" && remaining > 0;
    const remainingLabel = formatRemaining(remaining);

    const textBefore = (settings.text_before || "Order before {cutoff} for Same-Day Dispatch.")
        .replace("{cutoff}", settings.cutoff_label || "2:00 PM");
    const textAfter = settings.text_after || "Order now and your order will dispatch the next business day.";

    return (
        <div
            className={`inline-flex items-center gap-2.5 ${compact ? "px-3 py-1.5 text-xs" : "px-4 py-2.5 text-sm"} rounded-full border border-emerald-500/30 bg-gradient-to-r from-emerald-500/[0.08] to-emerald-500/[0.02] ${className}`}
            data-testid="dispatch-countdown"
            role="status"
            aria-live="polite"
        >
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500/15">
                <Truck size={compact ? 12 : 14} className="text-emerald-300" />
            </span>
            {isSameDay && remainingLabel ? (
                <span className="text-emerald-100">
                    <span className="font-display font-bold text-emerald-300 mr-1.5" data-testid="dispatch-countdown-time">
                        Order within {remainingLabel}
                    </span>
                    <span className="text-emerald-200/80">for Same-Day Dispatch.</span>
                </span>
            ) : (
                <span className="inline-flex items-center gap-1.5 text-emerald-100/90">
                    <Clock size={compact ? 11 : 13} className="text-emerald-300/80" />
                    {isSameDay ? textBefore : textAfter}
                </span>
            )}
        </div>
    );
};
